import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, XCircle } from 'lucide-react';
import { Command as CommandPrimitive } from 'cmdk';
import { useVirtualizer } from '@tanstack/react-virtual';

import {
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { cn } from '@/lib/utils.ts';

import type { IItemData } from '../../api/HandRequest.interface';

type AutoCompleteProps = {
  options: IItemData[];
  emptyMessage: string;
  value?: IItemData | null;
  onValueChange?: (value: IItemData | null) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  selectItemRef: React.MutableRefObject<HTMLInputElement | null>;
  fnToGoToQtyRef: () => void;
};

export function AutoCompleteItems({
  options,
  placeholder,
  emptyMessage,
  value,
  onValueChange,
  disabled,
  isLoading = false,
  selectItemRef,
  fnToGoToQtyRef,
}: AutoCompleteProps) {
  const parentRef = React.useRef<HTMLDivElement>(null);

  const [isOpen, setOpen] = useState(false);

  const [selected, setSelected] = useState<IItemData | undefined>(
    value as IItemData,
  );

  const [selectedMemory, setSelectedMemory] = useState<IItemData | undefined>(
    value as IItemData,
  );

  const [inputValue, setInputValue] = useState<string>(value?.Name || '');

  const filteredOptions = options.filter((option) =>
    option.Name.toLowerCase().startsWith(inputValue.toLowerCase()),
  );

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 5,
  });

  const virtualOptions = virtualizer.getVirtualItems();

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }
      const input = selectItemRef.current;
      if (!input) {
        return;
      }

      if (!isOpen) {
        setOpen(true);
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        if (selected) {
          onValueChange?.(selected);
          setSelected(undefined);
          fnToGoToQtyRef();
          return;
        }
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (selected) {
          const currentIndex = filteredOptions.findIndex(
            (option) => option.ID === selected.ID,
          );
          if (currentIndex < filteredOptions.length - 1) {
            const nextIndex = currentIndex + 1;
            setSelected(filteredOptions[nextIndex]);
            setSelectedMemory(filteredOptions[nextIndex]);
            virtualizer.scrollToIndex(nextIndex);
          } else {
            setSelected(filteredOptions[0]);
            setSelectedMemory(filteredOptions[0]);
            virtualizer.scrollToIndex(0);
          }
        } else if (selectedMemory && !inputValue) {
          setSelected(selectedMemory);
          virtualizer.scrollToIndex(
            filteredOptions.findIndex(
              (option) => option.ID === selectedMemory.ID,
            ),
          );
        } else {
          setSelected(filteredOptions[0]);
          setSelectedMemory(filteredOptions[0]);
          virtualizer.scrollToIndex(0);
        }
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (selected) {
          const currentIndex = filteredOptions.findIndex(
            (option) => option.ID === selected?.ID,
          );

          if (currentIndex > 0) {
            const prevIndex = currentIndex - 1;
            setSelected(filteredOptions[prevIndex]);
            setSelectedMemory(filteredOptions[prevIndex]);
            virtualizer.scrollToIndex(prevIndex);
          } else {
            setSelected(filteredOptions[filteredOptions.length - 1]);
            setSelectedMemory(filteredOptions[filteredOptions.length - 1]);
            virtualizer.scrollToIndex(filteredOptions.length - 1);
          }
        } else {
          setSelected(filteredOptions[filteredOptions.length - 1]);
          setSelectedMemory(filteredOptions[filteredOptions.length - 1]);
          virtualizer.scrollToIndex(filteredOptions.length - 1);
        }
      }

      if (event.key === 'Escape') {
        input.blur();
      }
    },
    [
      disabled,
      selectItemRef,
      isOpen,
      selected,
      onValueChange,
      fnToGoToQtyRef,
      selectedMemory,
      inputValue,
      filteredOptions,
      virtualizer,
    ],
  );

  const clearInput = useCallback(() => {
    setInputValue('');
    setSelected(undefined);
    setSelectedMemory(undefined);
    onValueChange?.(null);
    setOpen(false);
  }, [onValueChange]);

  const handleSelectOption = useCallback(
    (selectedOption: IItemData) => {
      setInputValue(selectedOption.Name);
      setSelected(selectedOption);
      setSelectedMemory(selectedOption);
      onValueChange?.(selectedOption);

      setTimeout(() => {
        selectItemRef?.current?.blur();
        setOpen(false); // Re-open the list
        const index = filteredOptions.findIndex(
          (option) => option.ID === selectedOption.ID,
        );
        virtualizer.scrollToIndex(index);
      }, 0);
    },
    [filteredOptions, onValueChange, selectItemRef, virtualizer],
  );

  const handleBlur = useCallback(() => {
    setOpen(false);
    if (selected) {
      setInputValue(selected?.Name);
    }
  }, [selected]);

  useEffect(() => {
    // Define the event listener function
    const forceReset: EventListener = (event) => {
      // TypeScript may infer this as `Event`, so you may need to cast it
      const keyboardEvent = event as unknown as KeyboardEvent;
      if (
        keyboardEvent.ctrlKey &&
        keyboardEvent.altKey &&
        keyboardEvent.key === 'ArrowUp' &&
        !disabled
      ) {
        setInputValue('');
        setOpen(true);
        selectItemRef.current?.focus();
      }
    };

    // Attach the event listener to the document
    document.addEventListener('keydown', forceReset);

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener('keydown', forceReset);
    };
  }, [disabled, selectItemRef]);

  useEffect(() => {
    const selectedOptionIndex = filteredOptions.findIndex(
      (option) => option.ID === selectedMemory?.ID,
    );
    if (selectedOptionIndex === -1 && isOpen) {
      setTimeout(() => {
        virtualizer.scrollToIndex(0);
      }, 0);
    } else {
      setTimeout(() => {
        virtualizer.scrollToIndex(selectedOptionIndex);
      }, 0);
    }
  }, [filteredOptions, isOpen, selectedMemory?.ID, value, virtualizer]);

  useEffect(() => {
    if (!value) {
      setTimeout(() => {
        setInputValue('');
      }, 0);
    } else {
      setInputValue(value.Name);
    }
  }, [value]);

  return (
    <CommandPrimitive
      className="mb-2 w-full rounded-md border border-indigo-300 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 dark:border-indigo-600 dark:focus-within:ring-indigo-400"
      onKeyDown={handleKeyDown}
    >
      <div className="relative flex h-10 items-center justify-start bg-indigo-50 dark:bg-indigo-950">
        {inputValue && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearInput}
            className="absolute left-0 top-0 z-10 ml-2 mt-1.5 h-7 w-7 rounded-full p-0 hover:bg-indigo-200 hover:text-indigo-700 focus:ring-2 focus:ring-indigo-500 dark:hover:bg-indigo-800 dark:hover:text-indigo-300"
            aria-label="Clear input"
          >
            <XCircle className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
          </Button>
        )}
        <CommandInput
          ref={selectItemRef}
          autoFocus
          value={inputValue}
          onValueChange={isLoading ? undefined : setInputValue}
          onBlur={handleBlur}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full min-w-80 text-lg text-indigo-900 placeholder-indigo-400 focus:outline-none dark:text-indigo-100 dark:placeholder-indigo-500',
            inputValue ? 'pl-10' : '',
          )}
        />
      </div>
      <div className="relative mt-1">
        <div
          className={cn(
            'absolute top-0 z-10 w-full rounded-xl border border-indigo-200 bg-white shadow-lg outline-none animate-in fade-in-0 zoom-in-95 dark:border-indigo-800 dark:bg-gray-950',
            isOpen ? 'block' : 'hidden',
          )}
        >
          <CommandList
            ref={parentRef}
            className="h-8 rounded-lg bg-white ring-1 ring-indigo-200 dark:bg-gray-950 dark:ring-indigo-800"
          >
            {isLoading ? (
              <CommandPrimitive.Loading>
                <div className="p-1">
                  <Skeleton className="h-8 w-full bg-indigo-100 dark:bg-indigo-900" />
                </div>
              </CommandPrimitive.Loading>
            ) : null}
            {filteredOptions.length > 0 && !isLoading ? (
              <CommandGroup
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualOptions.map((row) => {
                  const option = filteredOptions[row.index];
                  const isSelected = selected?.ID === option.ID;

                  return (
                    <CommandItem
                      key={option.ID}
                      value={option.Name}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onSelect={() => handleSelectOption(option)}
                      className={cn(
                        'flex w-full items-center justify-between gap-1 text-indigo-900 hover:bg-indigo-100 dark:text-indigo-100 dark:hover:bg-indigo-900',
                        isSelected
                          ? 'bg-indigo-200 text-indigo-900 dark:bg-indigo-800 dark:text-indigo-100'
                          : null,
                      )}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: `${row?.size}px`,
                        transform: `translateY(${row?.start}px)`,
                      }}
                    >
                      <div className="flex items-center font-bold">
                        {isSelected ? (
                          <Check className="mr-1 w-4 text-indigo-600 dark:text-indigo-400" />
                        ) : null}
                        {option.Name}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
            {!isLoading ? (
              <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm text-indigo-500 dark:text-indigo-400">
                {emptyMessage}
              </CommandPrimitive.Empty>
            ) : null}
          </CommandList>
        </div>
      </div>
    </CommandPrimitive>
  );
}
